// @ts-nocheck
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import validatePassword from "@insite/client-framework/Store/CommonHandlers/ValidatePassword";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import updatePassword from "@insite/client-framework/Store/Context/Handlers/UpdatePassword";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxPresentationProps } from "@insite/mobius/Checkbox";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import React, { ReactNode, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

/* Starter Custom Code - Start */
import FormActions from "@store/Library/Components/Toolkit/FormActions";
import FormWrapper from "@store/Library/Components/Toolkit/FormWrapper";
import PasswordRequirements, {
    PasswordRequirementsPresentationProps,
} from "@store/Library/Components/Toolkit/PasswordRequirements";
import { boxStyles } from "@store/Themes/common/mixins/box";
import { typographyTitleStyles } from "@store/Themes/common/mixins/typography";
import { formGridProps } from "@store/Themes/common/props/form";
import { pxToRemArray } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { spacingStyles } from "@toolkit/Themes/common/mixins/spacing";
/* Starter Custom Code - End */

interface OwnProps {
    enteredUserName: string;
    enteredPassword: string;
    instructionsText?: string;
    onPasswordChanged: (newPassword: string) => void;
}

const mapStateToProps = (state: ApplicationState) => ({
    accountSettings: getSettingsCollection(state).accountSettings,
});

const mapDispatchToProps = {
    validatePassword,
    updatePassword,
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext;

export interface SignInChangePasswordFormStyles {
    form?: InjectableCss;
    instructionsText?: TypographyPresentationProps;
    passwordGridContainer?: GridContainerProps;
    userNameGridItem?: GridItemProps;
    userNameTextField?: TextFieldPresentationProps;
    existingPasswordGridItem?: GridItemProps;
    existingPasswordTextField?: TextFieldPresentationProps;
    newPasswordGridItem?: GridItemProps;
    newPasswordTextField?: TextFieldPresentationProps;
    confirmNewPasswordGridItem?: GridItemProps;
    confirmNewPasswordTextField?: TextFieldPresentationProps;
    showPasswordsGridItem?: GridItemProps;
    showPasswordsCheckbox?: CheckboxPresentationProps;
    passwordRequirementsTitle?: TypographyPresentationProps;
    passwordRequirementsGridContainer?: GridContainerProps;
    passwordRequirementsGridItem?: GridItemProps;
    changePasswordButton?: ButtonPresentationProps;
    // Starter Custom Code
    passwordRequirements?: PasswordRequirementsPresentationProps;
}

export const signInChangePasswordFormStyles: SignInChangePasswordFormStyles = {
    instructionsText: { variant: "p" },
    passwordGridContainer: formGridProps.gridContainer,
    userNameGridItem: { width: 12 },
    existingPasswordGridItem: { width: 12 },
    newPasswordGridItem: { width: 12 },
    confirmNewPasswordGridItem: { width: 12 },
    showPasswordsGridItem: { width: 12 },
    passwordRequirementsGridItem: { width: 12 },
    passwordRequirements: {
        overridedStyles: {
            wrapper: {
                css: css`
                    ${boxStyles(pxToRemArray([25]))}
                    width: 100%;
                `,
            },
            title: {
                variant: null as any,
                forwardAs: "p",
                css: css`
                    ${typographyTitleStyles(pxToRemArray([18]))}
                    ${spacingStyles(null, "0.5em")}
                    line-height: ${x => x.theme.design.typography.lineHeight.semi};
                `,
            },
        },
    },
};

const styles = signInChangePasswordFormStyles;
const StyledForm = getStyledWrapper("form");

const SignInChangePasswordForm = ({
    enteredUserName,
    enteredPassword,
    instructionsText,
    accountSettings,
    toaster,
    onPasswordChanged,
    validatePassword,
    updatePassword,
}: Props) => {
    const [userName, setUserName] = useState(enteredUserName);
    const [password, setPassword] = useState(enteredPassword);
    const [newPassword, setNewPassword] = useState("");
    const [newPasswordErrorMessage, setNewPasswordErrorMessage] = useState<ReactNode>("");
    const [confirmNewPassword, setConfirmNewPassword] = useState("");
    const [showValidation, setShowValidation] = useState(false);
    const [error, setError] = useState(false);
    const [showPasswords, setShowPasswords] = useState(false);

    useEffect(() => {
        setError(
            !!newPasswordErrorMessage || !newPassword || !confirmNewPassword || confirmNewPassword !== newPassword,
        );
    }, [newPassword, newPasswordErrorMessage, confirmNewPassword]);

    const {
        passwordMinimumLength,
        passwordRequiresUppercase,
        passwordRequiresSpecialCharacter,
        passwordRequiresLowercase,
        passwordRequiresDigit,
    } = accountSettings;

    const userNameChangeHandler = (event: React.FormEvent<HTMLInputElement>) => {
        setUserName(event.currentTarget.value);
    };

    const passwordChangeHandler = (event: React.FormEvent<HTMLInputElement>) => {
        setPassword(event.currentTarget.value);
    };

    const newPasswordChangeHandler = (event: React.FormEvent<HTMLInputElement>) => {
        validatePassword({
            password: event.currentTarget.value,
            onComplete: errorMessage => {
                setNewPasswordErrorMessage(errorMessage);
            },
        });
        setNewPassword(event.currentTarget.value);
    };

    const confirmNewPasswordChangeHandler = (event: React.FormEvent<HTMLInputElement>) => {
        setConfirmNewPassword(event.currentTarget.value);
    };

    const savePasswordClickHandler = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        event.stopPropagation();

        if (error || newPasswordErrorMessage) {
            setShowValidation(true);
            return;
        }

        updatePassword({
            userName,
            password,
            newPassword,
            onApiResponse: onPasswordChangeApiResponse,
        });
    };

    const onPasswordChangeApiResponse = (error?: string) => {
        if (error) {
            toaster.addToast({
                body: error,
                messageType: "danger",
            });
            return;
        }

        toaster.addToast({
            body: translate("Password Updated"),
            messageType: "success",
        });

        onPasswordChanged(newPassword);
    };

    const confirmNewPasswordError =
        showValidation && newPassword && confirmNewPassword && newPassword !== confirmNewPassword
            ? siteMessage("CreateNewAccountInfo_PasswordCombination_DoesNotMatch")
            : "";

    return (
        <FormWrapper onlyFocus /* Starter Custom Code */>
            <StyledForm {...styles.form} onSubmit={savePasswordClickHandler} noValidate>
                {instructionsText && (
                    <Typography {...styles.instructionsText} /* Starter Custom Code */>
                        {parse(instructionsText, parserOptions)}
                    </Typography>
                )}
                <GridContainer {...styles.passwordGridContainer}>
                    <GridItem {...styles.userNameGridItem}>
                        <TextField
                            label={translate("Username")}
                            onInput={userNameChangeHandler}
                            autoComplete="user-name"
                            value={userName}
                            {...styles.userNameTextField}
                        />
                    </GridItem>
                    <GridItem {...styles.existingPasswordGridItem}>
                        <TextField
                            type={showPasswords ? "text" : "password"}
                            label={translate("Existing Password")}
                            onInput={passwordChangeHandler}
                            autoComplete="existing-password"
                            value={password}
                            data-test-selector="existingPassword"
                            {...styles.existingPasswordTextField}
                        />
                    </GridItem>
                    <GridItem {...styles.newPasswordGridItem}>
                        <TextField
                            type={showPasswords ? "text" : "password"}
                            label={translate("New Password")}
                            onInput={newPasswordChangeHandler}
                            error={newPasswordErrorMessage}
                            autoComplete="new-password"
                            data-test-selector="newPassword"
                            {...styles.newPasswordTextField}
                        />
                    </GridItem>
                    <GridItem {...styles.confirmNewPasswordGridItem}>
                        <TextField
                            type={showPasswords ? "text" : "password"}
                            label={translate("Confirm New Password")}
                            onInput={confirmNewPasswordChangeHandler}
                            error={confirmNewPasswordError}
                            autoComplete="new-password"
                            data-test-selector="confirmNewPassword"
                            {...styles.confirmNewPasswordTextField}
                        />
                    </GridItem>
                    <GridItem {...styles.showPasswordsGridItem}>
                        <Checkbox
                            onChange={() => {
                                setShowPasswords(!showPasswords);
                            }}
                            checked={showPasswords}
                            // Starter Removed - hiddenIconText
                            {...styles.showPasswordsCheckbox}
                        >
                            {translate("Show Passwords")}
                        </Checkbox>
                    </GridItem>
                    {/* Starter Custom Code - Start */}
                    <GridItem {...styles.passwordRequirementsGridItem}>
                        <PasswordRequirements {...styles.passwordRequirements} />
                    </GridItem>
                    {/* Starter Custom Code - End */}
                </GridContainer>
                <FormActions /* Starter Custom Code */>
                    <Button
                        {...styles.changePasswordButton}
                        type="submit"
                        disabled={!userName || !password || !newPassword || !confirmNewPassword}
                        data-test-selector="saveNewPassword"
                    >
                        {translate("Save New Password")}
                    </Button>
                </FormActions>
            </StyledForm>
        </FormWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withToaster(SignInChangePasswordForm));
